/**
 * =========================================================================================
 * Billing Payments - List Component
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import * as UTable from 'ubs-framework/v2/common/UTable';
import { useDispatch, useSelector } from 'react-redux';
import * as Icon from 'ubs-icons';
import UPopup from 'ubs-framework/v2/UPopup';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UTags from 'ubs-framework/v2/UTags';
import UButton from 'ubs-framework/v2/UButton';
import { getKeyName, numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { BILLING_CYCLE_OPTIONS } from '../../../../../common/constant';

const PAYMENT_STATUS_OPTIONS = [
    { value: 'SUCCESS', label: 'Success', color: '#60B158' },
    { value: 'FAILED', label: 'Failed', color: '#FF6961' },
    { value: 'CANCELLED', label: 'Cancelled', color: '#999999' },
    { value: 'PENDING', label: 'Pending', color: '#FFA700' },
];

const PaymentsList = React.memo(({ paymentsList, totalRecords, dataLoading, resumeLoadingId, onResumePayment, cancelSubscription }) => {
    const DISPATCH = useDispatch();
    const _FILTER = useSelector(e => e?.filter?.billingPayments);

    const pagination = (payload) => {
        DISPATCH({ type: 'SET_BILLING_PAYMENTS', payload });
    };

    const getStatusTag = (status) => {
        const opt = PAYMENT_STATUS_OPTIONS.find(o => o.value === status);
        return <UTags className="u_ib" title={opt?.label || status || '-'} color={opt?.color || '#999999'} />;
    };

    const header = useMemo(() => [
        {
            title: 'Payment For',
            param: 'title',
            render: data => data?.paymentFor?.title || '-'
        },
        {
            title: 'Plan',
            param: 'planCode',
            render: data => data?.paymentFor?.planCode
        },
        {
            title: 'Billing Cycle',
            param: 'billingCycle',
            render: data => (
                <span>{getKeyName(BILLING_CYCLE_OPTIONS, data?.paymentFor?.billingCycle)?.label || data?.paymentFor?.billingCycle}</span>
            )
        },
        {
            title: 'Date',
            param: 'paymentDate',
            render: data => <span className="u_ws-nowrap">{dateFormatter({ date: data?.paymentDate || data?.scheduleDate, format: 'DD MMM YYYY, hh:mm A' })}</span>
        },
        {
            title: 'Seats',
            param: 'seatCount',
            render: data => data?.paymentFor?.seatCount || 0
        },
        {
            title: 'Amount',
            param: 'amount',
            render: data => (
                <span className="u_fw-500">{numberWithComaToFixed(data?.amount, data?.currency)}</span>
            )
        },
        {
            title: 'Status',
            param: 'status',
            render: data => getStatusTag(data?.status)
        },
        {
            title: 'Actions',
            render: data => (
                data?.action === 'RESUME_PAYMENT' && <UPopup
                    params={{ position: "bottom right" }}
                    desc={
                        <ul className="u_menu_list">
                            {data?.action === 'RESUME_PAYMENT' && <li onClick={() => onResumePayment(data)}>
                                <Icon.CreditCard fill={COLOR?.u_blue} size={16} className="u_mr-8" /> {resumeLoadingId === data?.paymentId ? 'Processing...' : 'Pay Now'}
                            </li>}
                            {data?.action === 'RESUME_PAYMENT' && <li onClick={() => cancelSubscription(data)}>
                                <Icon.CloseMarkRound fill={COLOR?.u_red} size={16} className="u_mr-8" /> {resumeLoadingId === data?.paymentId ? 'Processing...' : 'Cancel Modification'}
                            </li>}
                        </ul>
                    }
                >
                    <span className={`u_cp`}>
                        <Icon.HorizontalMenu size={20} fill={COLOR.u_icon_bg} />
                    </span>
                </UPopup>
            )
        },
    ], [resumeLoadingId]);

    return (
        <React.Fragment>
            <div className="u_flex u_flex-col u_h-full">
                <UTable.Table
                    limit={_FILTER?.limit}
                    currentPage={_FILTER?.currentPage}
                    totalRecords={totalRecords || 0}
                    onPagination={pagination}
                    header={header}
                    data={paymentsList}
                    dataLoading={dataLoading}
                    skeletonColumn={7}
                    className="u_table-resource u_table-resource-nowrap u_table-resource-no-sticky u_table-resource-first-column-sticky u_h-full u_alert-list"
                />
            </div>
        </React.Fragment>
    );
});

export default PaymentsList;
